import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import MaterialSymbol from '../common/MaterialSymbol';
import {
    LearningPath,
    LearningPathCourse,
    LearningPathEnrollment,
    learningPathService,
} from '../../services/learningPathService';

/** Fortschritt/Status einer eigenen Kurs-Einschreibung (courseId → Info). */
export interface CourseProgressInfo {
    status: string;
    progress: number;
}

interface LearningPathCardProps {
    path: LearningPath;
    enrollment?: LearningPathEnrollment | null;
    onEnroll?: (pathId: number) => void;
    onView?: (pathId: number) => void;
    loading?: boolean;
    /**
     * Eigene Kurs-Einschreibungen (courseId → Status/Fortschritt) für den
     * Balken "Fortschritt im aktuellen Kurs" (Kunden-Feedback). Fehlt die
     * Map (Fetch fehlgeschlagen), fällt der Balken auf den Pfad-Fortschritt
     * zurück.
     */
    courseProgress?: Map<number, CourseProgressInfo>;
}

const DIFFICULTY_CLASS: Record<string, string> = {
    beginner: 'diff-ein',
    intermediate: 'diff-fort',
    advanced: 'diff-exp',
};

function extractCourseId(course: LearningPathCourse['course'] | number | string | null | undefined): number | null {
    if (course === null || course === undefined) return null;
    if (typeof course === 'number') return course;
    if (typeof course === 'object' && typeof course.id === 'number') return course.id;
    if (typeof course === 'string') {
        const match = course.match(/\/courses\/(\d+)/);
        if (match) return parseInt(match[1], 10);
    }
    return null;
}

const LearningPathCard: React.FC<LearningPathCardProps> = ({
    path,
    enrollment,
    onEnroll,
    onView,
    loading = false,
    courseProgress,
}) => {
    const translate = useTranslate();

    const [open, setOpen] = useState(false);
    const [pathCourses, setPathCourses] = useState<LearningPathCourse[] | null>(null);
    const [coursesLoading, setCoursesLoading] = useState(false);

    const isPending = enrollment?.status === 'pending_approval';
    const isRejected = enrollment?.status === 'rejected';
    const isEnrolled = !!enrollment && !isPending && !isRejected;
    const isCompleted = enrollment?.status === 'completed';
    const pathProgress = enrollment ? parseFloat(enrollment.progressPercentage) : 0;
    const enrollmentMode = path.enrollmentMode || 'self';

    const loadCourses = async () => {
        if (pathCourses !== null || coursesLoading) return;
        try {
            setCoursesLoading(true);
            const data = await learningPathService.getPathCourses(path.id);
            setPathCourses([...data].sort((a, b) => a.sortOrder - b.sortOrder));
        } catch {
            // Ohne Kursliste degradiert die Karte sauber (Balken = Pfad-Fortschritt).
            setPathCourses([]);
        } finally {
            setCoursesLoading(false);
        }
    };

    // Für eingeschriebene, nicht abgeschlossene Pfade wird die Kursliste sofort
    // geladen: sie bestimmt den AKTUELLEN Kurs für den Fortschrittsbalken.
    useEffect(() => {
        if (isEnrolled && !isCompleted) {
            loadCourses();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnrolled, isCompleted, path.id]);

    const handleToggle = () => {
        if (!open) loadCourses();
        setOpen(prev => !prev);
    };

    // Aktueller Kurs = erster (nach sortOrder) noch nicht abgeschlossener Kurs.
    let currentCourseId: number | null = null;
    let currentCourseProgress = 0;
    if (pathCourses && pathCourses.length > 0 && courseProgress) {
        for (const pc of pathCourses) {
            const courseId = extractCourseId(pc.course);
            if (courseId === null) continue;
            if (courseProgress.get(courseId)?.status === 'completed') continue;
            currentCourseId = courseId;
            currentCourseProgress = courseProgress.get(courseId)?.progress ?? 0;
            break;
        }
    }

    // Balken-Semantik (Kunden-Feedback, bindend): Fortschritt im AKTUELLEN Kurs.
    // Fallbacks: abgeschlossen → 100 %, nicht eingeschrieben → 0 %,
    // Kursdaten (noch) nicht verfügbar → Pfad-Fortschritt.
    let barLabel = translate('learningPaths.progress', { _: 'Fortschritt' });
    let barValue = 0;
    if (isCompleted) {
        barValue = 100;
    } else if (isEnrolled) {
        if (pathCourses && pathCourses.length > 0 && courseProgress) {
            if (currentCourseId !== null) {
                barLabel = 'Aktueller Kurs';
                barValue = currentCourseProgress;
            } else {
                barValue = 100;
            }
        } else {
            barValue = pathProgress;
        }
    }
    // Vorlage: 0 % wird als schmaler 2%-Streifen gezeichnet.
    const barWidth = Math.min(100, Math.max(barValue, 2));

    const difficultyClass = DIFFICULTY_CLASS[path.difficulty] || 'diff-ein';
    const coursesLabel = translate('learningPaths.courses', { _: 'Kurse' });

    return (
        <article className={`pcard${open ? ' open' : ''}`}>
            <div
                className={`phero${path.thumbnailUrl ? ' has-thumb' : ''}`}
                style={path.thumbnailUrl ? { backgroundImage: `url(${path.thumbnailUrl})` } : undefined}
            >
                <span className={`pbadge ${difficultyClass}`}>
                    {learningPathService.getDifficultyLabel(path.difficulty)}
                </span>
                {isEnrolled && (
                    <span className="penrolled">
                        <MaterialSymbol name="check" />
                        {isCompleted
                            ? translate('learningPaths.completed', { _: 'Abgeschlossen' })
                            : 'Eingeschrieben'}
                    </span>
                )}
                {isPending && (
                    <span className="penrolled pend">
                        <MaterialSymbol name="hourglass_top" />
                        Anfrage ausstehend
                    </span>
                )}
                {!path.thumbnailUrl && <MaterialSymbol name="school" className="pcap" />}
            </div>

            <div className="pbody">
                {path.courseCount != null && (
                    <span className="pcount">
                        <MaterialSymbol name="layers" />
                        {path.courseCount} {coursesLabel}
                    </span>
                )}
                <h3>{path.title}</h3>
                {path.description && <p className="pdesc">{path.description}</p>}

                <div className="pmeta">
                    {path.estimatedDuration ? (
                        <span>
                            <MaterialSymbol name="schedule" />
                            {learningPathService.formatDuration(path.estimatedDuration)}
                        </span>
                    ) : null}
                    {path.courseCount != null && (
                        <span>
                            <MaterialSymbol name="layers" />
                            {path.courseCount} {coursesLabel}
                        </span>
                    )}
                </div>

                <div className="pprog">
                    <div className="pprog-head">
                        <span>{barLabel}</span>
                        <span>{learningPathService.formatProgress(barValue)}</span>
                    </div>
                    <div className="pbar">
                        <i style={{ width: `${barWidth}%` }} />
                    </div>
                </div>

                <button className="ptoggle" type="button" onClick={handleToggle}>
                    Kurse ansehen <MaterialSymbol name="expand_more" />
                </button>
                {open && (
                    <div className="pcourses">
                        {coursesLoading ? (
                            <div className="pcrow todo">
                                <span className="pcdot" />
                                Kurse werden geladen …
                            </div>
                        ) : !pathCourses || pathCourses.length === 0 ? (
                            <div className="pcrow todo">
                                <span className="pcdot" />
                                Keine Kurse im Pfad
                            </div>
                        ) : (
                            pathCourses.map(pc => {
                                const courseId = extractCourseId(pc.course);
                                const isDone =
                                    courseId !== null &&
                                    courseProgress?.get(courseId)?.status === 'completed';
                                const isCurrent =
                                    !isDone && courseId !== null && courseId === currentCourseId;
                                const title =
                                    typeof pc.course === 'object' && pc.course
                                        ? pc.course.title
                                        : '';
                                return (
                                    <div
                                        key={pc.id}
                                        className={`pcrow ${isDone ? 'done' : isCurrent ? 'cur' : 'todo'}`}
                                    >
                                        {isDone ? (
                                            <MaterialSymbol name="check" />
                                        ) : isCurrent ? (
                                            <MaterialSymbol name="play_arrow" filled />
                                        ) : (
                                            <span className="pcdot" />
                                        )}
                                        {title}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {isPending ? (
                    <span className="pstatus">
                        <MaterialSymbol name="hourglass_top" />
                        Anfrage ausstehend
                    </span>
                ) : isRejected ? (
                    <button
                        className="cbtn outline pmain"
                        type="button"
                        onClick={() => onEnroll?.(path.id)}
                        disabled={loading}
                    >
                        Erneut anfragen
                    </button>
                ) : isEnrolled ? (
                    <button
                        className={`cbtn ${isCompleted ? 'outline' : 'solid'} pmain`}
                        type="button"
                        onClick={() => onView?.(path.id)}
                    >
                        {isCompleted ? (
                            translate('learningPaths.viewCertificate', { _: 'Ansehen' })
                        ) : (
                            <>
                                <MaterialSymbol name="play_arrow" filled />
                                {translate('learningPaths.continue', { _: 'Fortsetzen' })}
                            </>
                        )}
                    </button>
                ) : enrollmentMode === 'self' ? (
                    <button
                        className="cbtn solid pmain"
                        type="button"
                        onClick={() => onEnroll?.(path.id)}
                        disabled={loading}
                    >
                        {translate('learningPaths.enroll', { _: 'Einschreiben' })}
                    </button>
                ) : enrollmentMode === 'request' ? (
                    <button
                        className="cbtn outline pmain"
                        type="button"
                        onClick={() => onEnroll?.(path.id)}
                        disabled={loading}
                    >
                        Einschreibung anfragen
                    </button>
                ) : (
                    <span className="pnote">Nur über Zuweisung</span>
                )}
            </div>
        </article>
    );
};

export default LearningPathCard;
